import React, { createContext, useMemo } from "react"
import { renderNodeRule, renderRule, StructuredText } from "react-datocms"
import { isBlockquote, isHeading, isList, isParagraph } from "datocms-structured-text-utils"
import dynamic from "next/dynamic"
import Loading from "@/components/common/Loading"
import useCustomContext from "@/hooks/useCustomContext"
import { processStructuredTextMargins } from "@/components/common/StructuredText/utils"
import Inline from "@/components/common/StructuredText/components/Inline"
import Link from "@/components/common/StructuredText/components/Link"
import Block from "@/components/common/StructuredText/components/Block"
import Heading from "@/components/common/StructuredText/components/Heading"
import List from "@/components/common/StructuredText/components/List"
import Paragraph from "@/components/common/StructuredText/components/Paragraph"
import Quote from "@/components/common/StructuredText/components/Quote"

// const Block = dynamic(() => import("@/components/common/StructuredText/components/Block"), {
// 	loading: () => <Loading />,
// 	ssr: false,
// })
//
// const Link = dynamic(() => import("@/components/common/StructuredText/components/Link"), {
// 	loading: () => <Loading />,
// 	ssr: false,
// })
//
// const Inline = dynamic(() => import("@/components/common/StructuredText/components/Inline"), {
// 	loading: () => <Loading />,
// 	ssr: true, //testing
// })
//
// const List = dynamic(() => import("@/components/common/StructuredText/components/List"), {
// 	loading: () => <Loading />,
// 	ssr: false,
// })
//
// const Paragraph = dynamic(() => import("@/components/common/StructuredText/components/Paragraph"), {
// 	loading: () => <Loading />,
// 	ssr: false,
// })
//
// const Heading = dynamic(() => import("@/components/common/StructuredText/components/Heading"), {
// 	loading: () => <Loading />,
// 	ssr: false,
// })
//
// const Quote = dynamic(() => import("@/components/common/StructuredText/components/Quote"), {
// 	loading: () => <Loading />,
// 	ssr: false,
// })

const INIT_CONTEXT = {
	indent: 5,
	width: 6,
}

const StructuredContext = createContext(INIT_CONTEXT)

const StructuredTextContent = ({ data, indent = INIT_CONTEXT.indent, width = INIT_CONTEXT.width }) => {
	const context = useMemo(
		() => ({
			indent,
			width,
		}),
		[indent, width],
	)

	const processedData = useMemo(() => processStructuredTextMargins(data), [data])

	return (
		<StructuredContext.Provider value={context}>
			<StructuredText
				data={processedData}
				renderInlineRecord={({ record }) => <Inline key={record?.id || ""} data={record} />}
				renderLinkToRecord={({ record, children }) => (
					<Link key={record?.id || ""} data={record}>
						{children}
					</Link>
				)}
				renderBlock={(data) => <Block key={data.record.id} data={data.record} />}
				customNodeRules={[
					renderNodeRule(isHeading, ({ node, children, key }) => (
						<Heading key={key} data={node}>
							{children}
						</Heading>
					)),
					renderRule(isList, ({ node, children, key }) => (
						<List key={key} data={node}>
							{children}
						</List>
					)),
					renderRule(isParagraph, ({ node, children, key }) => (
						<Paragraph key={key} data={node}>
							{children}
						</Paragraph>
					)),
					renderRule(isBlockquote, ({ node, children, key }) => (
						<Quote key={key} data={node}>
							{children}
						</Quote>
					)),
				]}
			/>
		</StructuredContext.Provider>
	)
}

export function useStructuredContext(selector = undefined) {
	return useCustomContext(StructuredContext, selector)
}

export default StructuredTextContent